import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Textarea } from "@/components/ui/textarea"
import { adminApiService } from "@/features/admin/services/admin-api-service"
import type { Channel } from "@/features/admin/types"
import { Loader2, Settings } from "lucide-react"
import { toast } from "sonner"

interface ChannelSettingsModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  channel: Channel | null
  onSaved: () => void
}

export function ChannelSettingsModal({
  open,
  onOpenChange,
  channel,
  onSaved,
}: ChannelSettingsModalProps) {
  const [signatureEnabled, setSignatureEnabled] = useState(false)
  const [signatureTemplate, setSignatureTemplate] = useState("*{nome}*:\n\n")
  const [commentAutoReplyEnabled, setCommentAutoReplyEnabled] = useState(false)
  const [dmAutoReplyEnabled, setDmAutoReplyEnabled] = useState(true)
  const [acceptGroupMessages, setAcceptGroupMessages] = useState(false)
  const [transcribeAudio, setTranscribeAudio] = useState(true)
  const [processImages, setProcessImages] = useState(false)
  const [processFiles, setProcessFiles] = useState(false)
  const [saving, setSaving] = useState(false)

  const isInstagram = channel?.type === "instagram"
  const isWhatsApp = channel?.type === "whatsapp" || channel?.type === "whatsapp-business"

  useEffect(() => {
    if (open && channel) {
      setSignatureEnabled(channel.settings?.signature_enabled ?? false)
      setSignatureTemplate(channel.settings?.signature_template ?? "*{nome}*:\n\n")
      setCommentAutoReplyEnabled(channel.settings?.comment_auto_reply_enabled ?? false)
      setDmAutoReplyEnabled(channel.settings?.dm_auto_reply_enabled ?? true)
      setAcceptGroupMessages(channel.settings?.accept_group_messages ?? false)
      setTranscribeAudio(channel.transcribe_audio ?? true)
      setProcessImages(channel.process_images ?? false)
      setProcessFiles(channel.process_files ?? false)
    }
  }, [open, channel])

  const handleSave = async () => {
    if (!channel) return

    setSaving(true)
    try {
      await adminApiService.patchResource("channels", channel._id, {
        transcribe_audio: transcribeAudio,
        process_images: processImages,
        process_files: processFiles,
        settings: {
          ...channel.settings,
          signature_enabled: signatureEnabled,
          signature_template: signatureTemplate,
          comment_auto_reply_enabled: commentAutoReplyEnabled,
          dm_auto_reply_enabled: dmAutoReplyEnabled,
          accept_group_messages: acceptGroupMessages,
        },
      })
      toast.success("Configurações do canal salvas com sucesso")
      onSaved()
      onOpenChange(false)
    } catch (err) {
      console.error("Error saving channel settings:", err)
      toast.error("Erro ao salvar configurações do canal")
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Configurações do Canal
          </DialogTitle>
          <DialogDescription>
            {channel ? `Configurações para o canal "${channel.name}"` : "Configurações do canal"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="signature-enabled">Assinatura de mensagens</Label>
              <p className="text-muted-foreground text-sm">
                Adiciona assinatura do atendente nas mensagens enviadas
              </p>
            </div>
            <Switch
              id="signature-enabled"
              checked={signatureEnabled}
              onCheckedChange={setSignatureEnabled}
            />
          </div>

          {signatureEnabled && (
            <div className="space-y-2">
              <Label htmlFor="signature-template">Template da assinatura</Label>
              <Textarea
                id="signature-template"
                value={signatureTemplate}
                onChange={e => setSignatureTemplate(e.target.value)}
                placeholder="*{nome}*:\n\n"
                rows={3}
              />
              <p className="text-muted-foreground text-xs">
                Use <code className="bg-muted rounded px-1">{"{nome}"}</code> para o primeiro nome
                ou <code className="bg-muted rounded px-1">{"{nome_completo}"}</code> para o nome
                completo
              </p>
            </div>
          )}

          {isInstagram && (
            <>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="dm-auto-reply">Resposta automática a DMs</Label>
                  <p className="text-muted-foreground text-sm">
                    O agente de IA responde automaticamente quando alguém envia uma mensagem direta
                  </p>
                </div>
                <Switch
                  id="dm-auto-reply"
                  checked={dmAutoReplyEnabled}
                  onCheckedChange={setDmAutoReplyEnabled}
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="comment-auto-reply">Resposta automática a comentários</Label>
                  <p className="text-muted-foreground text-sm">
                    Envia uma mensagem privada (DM) automaticamente quando alguém comenta em um post
                  </p>
                </div>
                <Switch
                  id="comment-auto-reply"
                  checked={commentAutoReplyEnabled}
                  onCheckedChange={setCommentAutoReplyEnabled}
                />
              </div>
            </>
          )}

          {isWhatsApp && (
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="accept-group-messages">Mensagens de grupo</Label>
                <p className="text-muted-foreground text-sm">
                  Receber mensagens de grupos do WhatsApp. Apenas atendentes humanos poderão
                  responder, o agente de IA nunca responde em grupos.
                </p>
              </div>
              <Switch
                id="accept-group-messages"
                checked={acceptGroupMessages}
                onCheckedChange={setAcceptGroupMessages}
              />
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="transcribe-audio">Transcrever áudios</Label>
              <p className="text-muted-foreground text-sm">
                Transcreve automaticamente mensagens de áudio para que o agente de IA possa entender
                e responder
              </p>
            </div>
            <Switch
              id="transcribe-audio"
              checked={transcribeAudio}
              onCheckedChange={setTranscribeAudio}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="process-images">Processar imagens</Label>
              <p className="text-muted-foreground text-sm">
                Permite que o agente de IA visualize e entenda imagens enviadas pelos contatos
              </p>
            </div>
            <Switch
              id="process-images"
              checked={processImages}
              onCheckedChange={setProcessImages}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="process-files">Processar documentos</Label>
              <p className="text-muted-foreground text-sm">
                Permite que o agente de IA leia e entenda documentos (PDF, TXT, CSV, DOC, XLS)
                enviados pelos contatos
              </p>
            </div>
            <Switch id="process-files" checked={processFiles} onCheckedChange={setProcessFiles} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
